import { kNarrowViewportQuery } from "@/client/utils/displayMode";
import { getDisplayMode } from "@/client/utils/getDisplayMode";
import type { SampleTarget } from "@/client/utils/sample-targets";
import { createSignalWithValue, type RemoveListener } from "@/client/utils/signal";

/**
 * What a page element asks for when it wants the Sounds dock shown. `target`
 * is the dropzone that asked: a sample used while the dock is open lands
 * there without the chooser, since the person already said where it goes.
 */
export interface SoundsBrowserOpenRequest {
    readonly target?: SampleTarget;
}

export type SoundsBrowserOpener = (request: SoundsBrowserOpenRequest) => void;

/** Boxed, because a signal's value is read-only and a bare function would lose its call signature. */
interface SoundsBrowserOpenerEntry {
    readonly open: SoundsBrowserOpener;
}

/**
 * How a dropzone opens the Sounds dock without knowing it. The dock registers
 * an opener when it mounts (website only; the embedded hosts have no dock),
 * and a dropzone reads the signal to decide whether "Add a Splice sample" is
 * something it can offer. Mounting order does not matter: the dock mounts
 * after the app's UI, so a dropzone listens rather than checking once.
 */
export const soundsBrowserOpenerSignal = createSignalWithValue<
    SoundsBrowserOpenerEntry | undefined
>(undefined);

export const canOpenSoundsBrowser = (): boolean => soundsBrowserOpenerSignal.value !== undefined;

/** Returns whether a dock was there to open. */
export const openSoundsBrowser = (request: SoundsBrowserOpenRequest = {}): boolean => {
    const entry = soundsBrowserOpenerSignal.value;
    if (entry === undefined) {
        return false;
    }
    entry.open(request);
    return true;
};

/** Returns the unregister function. A later registration replaces an earlier one. */
export const registerSoundsBrowserOpener = (open: SoundsBrowserOpener): (() => void) => {
    const entry: SoundsBrowserOpenerEntry = { open };
    soundsBrowserOpenerSignal.notify(entry);
    return () => {
        if (soundsBrowserOpenerSignal.value === entry) {
            soundsBrowserOpenerSignal.notify(undefined);
        }
    };
};

let narrowQuery: MediaQueryList | undefined;
/** Created on first use: the module is imported by tests that have no window. */
const getNarrowQuery = (): MediaQueryList => {
    narrowQuery ??= window.matchMedia(kNarrowViewportQuery);
    return narrowQuery;
};

/**
 * Whether a drop target should lead with a tap that opens the Sounds dock:
 * the website at phone width, where nothing can be dragged in, with a dock
 * on the page to open. Never in the embedded hosts, which have no dock, and
 * never on a desktop, where the drag and the picker remain the offer.
 */
export const shouldOfferSoundsBrowserTap = (): boolean =>
    getDisplayMode() === "web" && getNarrowQuery().matches && canOpenSoundsBrowser();

/**
 * Both inputs to `shouldOfferSoundsBrowserTap` can change after a target is
 * built: the dock mounts after the app's UI, and a window can be resized
 * across the breakpoint. A target that renders copy from it listens here.
 */
export const listenSoundsBrowserTapOffer = (listener: () => void): RemoveListener => {
    const stopSignal = soundsBrowserOpenerSignal.listen(listener);
    const query = getNarrowQuery();
    query.addEventListener("change", listener);
    return () => {
        stopSignal();
        query.removeEventListener("change", listener);
    };
};
